import { type CreateEditorOptions, createEditor, type EditorHandle } from "@codeshare/editor";
import { useEffect, useRef } from "react";

interface CodeEditorProps extends Omit<CreateEditorOptions, "signal"> {
  onReady?: (handle: EditorHandle) => void;
}

/** Mounts a Monaco editor. Options are read once, at mount. */
export function CodeEditor({ onReady, ...options }: CodeEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const initial = useRef({ options, onReady });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Unmounting (including StrictMode's dev double mount) aborts creation while Monaco loads.
    const abort = new AbortController();
    let handle: EditorHandle | undefined;

    createEditor(container, { ...initial.current.options, signal: abort.signal }).then(
      (created) => {
        handle = created;
        initial.current.onReady?.(created);
      },
      (error: unknown) => {
        if (!abort.signal.aborted) throw error;
      },
    );

    return () => {
      abort.abort();
      handle?.dispose();
    };
  }, []);

  return <div ref={containerRef} className="code-editor" data-testid="code-editor" />;
}
